<template>
  <div class="n-message-composer">
    <div
      class="n-message-composer__input-container"
      :class="{
        'n-message-composer__input-container--focused': isInputFocused,
        'n-message-composer__input-container--disabled': disabled,
      }"
    >
      <textarea
        ref="input"
        class="n-message-composer__input"
        rows="1"
        placeholder="Enter your message..."
        v-model="messageText"
        :disabled="disabled"
        @input="onInput()"
        @focus="onFocus()"
        @blur="onBlur"
        @keypress.enter="onSend"
      ></textarea>
      <div class="n-message-composer__actions">
        <n-emoji-picker @emojiSelect="insertEmoji" :accentColour="accentColour"></n-emoji-picker>
        <n-button
          icon="send"
          variant="action"
          :disabled="messageText && messageText.length === 0"
          @click="onSend"
        ></n-button>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { onMounted, ref, nextTick } from 'vue';
import { debounce } from 'lodash';
import autosize from 'autosize';
import NButton from '../../control/button/button.vue';
import NEmojiPicker from '../emoji-picker/emoji-picker.vue';

defineOptions({
  name: 'n-message-composer',
});

defineProps({
  accentColour: {
    type: String,
    default: '#062b80',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: 'conversationRead'): void;
  (e: 'typingStart'): void;
  (e: 'typingStop'): void;
  (e: 'messageSend', messageText: string): void;
}>();

const messageText = ref('');

const input = ref<HTMLElement | null>(null);

const selectionStart = ref(0);
const selectionEnd = ref(0);
const isInputFocused = ref(false);
const sentTyping = ref(false);

onMounted(() => {
  autosize(input.value);
});

function onFocus() {
  isInputFocused.value = true;
}

function onBlur(event: any) {
  selectionStart.value = event.target.selectionStart;
  selectionEnd.value = event.target.selectionEnd;
  isInputFocused.value = false;
}

function insertEmoji(emoji: any) {
  messageText.value =
    messageText.value.substring(0, selectionStart.value) +
    emoji.native +
    messageText.value.substring(selectionEnd.value, messageText.value.length);

  nextTick(() => {
    setCursorPosition(selectionStart.value + 2);
    autosize.update(input.value);
  });
}

function onInput() {
  sendConversationRead();

  if (!sentTyping.value) {
    emit('typingStart');
    sentTyping.value = true;
  }
  stopTyping();
}

function setCursorPosition(pos: number) {
  input.value?.focus();
  (input.value as HTMLInputElement).setSelectionRange(pos, pos);
}

function onSend(e: KeyboardEvent): void {
  if (e) {
    e.preventDefault();
  }

  if (!messageText.value.trim().length) {
    messageText.value = '';
    return;
  }

  emit('messageSend', messageText.value);

  // cancel the stop typing event so we don't send stop typing after we've sent the message
  sentTyping.value = false;

  messageText.value = '';

  // reset height of textarea (in case it had to grow to accommodate text)
  nextTick(() => {
    autosize.update(input.value);
  });
}

function getOpacity() {
  if (messageText && messageText.value.length === 0) {
    return 0.4;
  }
  return 1;
}

function sendConversationRead() {
  emit('conversationRead');
}

function sendTypingStop() {
  emit('typingStop');
}

const stopTyping = debounce(() => {
  sendTypingStop();
  sentTyping.value = false;
}, 10000);
</script>

<style lang="scss" scoped>
@use '../../../styles/accessibility' as *;
@use 'emoji-mart-vue-fast/css/emoji-mart' as *;
.n-message-composer {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  background: white;
}

.n-message-composer__input-container {
  font-size: 0.8em;
  position: relative;
  display: flex;
  padding-top: 1em;
  padding-bottom: 1em;
  padding-left: 1.5em;
  align-items: center;
  background: transparent;
  transition: box-shadow 300ms;

  &--focused {
    transition: box-shadow 300ms;
    box-shadow: 0px 0px 40px 10px rgba(220, 220, 220, 0.5);
  }

  &--disabled {
    background-color: #f2f2f2;
  }
}

.n-message-composer__input {
  background: transparent;
  border: none;
  font-family: inherit;
  max-height: 6em;
  outline: none;
  padding: 0.3em;
  resize: none;
  width: 100%;
  &::placeholder {
    color: #ccc;
  }
  scrollbar-color: #eee transparent;
  scrollbar-width: thin;
  /* width */
  &::-webkit-scrollbar {
    width: 12px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: #eee;
    border-radius: 1em;
    border: 3px solid rgba(0, 0, 0, 0);
    background-clip: padding-box;
  }

  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: #ccc;
    border: 3px solid rgba(0, 0, 0, 0);
    background-clip: padding-box;
  }
}

.n-message-composer__actions {
  cursor: pointer;
  width: 7em;
  position: relative;
  align-items: center;
  display: flex;
  justify-content: flex-end;
  border-radius: 0.3em;
  margin-right: 20px;
  > div {
    margin-right: 4px;
  }
}

:deep(.n-tooltip__trigger) {
  display: flex;
}
</style>
